import { useContext, useEffect, useState } from 'react';
import { UserContext } from '../../../context/authContext';
import axios from 'axios';
import {
  Button,
  FormField,
  Input,
  Modal,
  Select
} from '@cloudscape-design/components';
import { ProjectContext } from '../../../context/projectContext';
import { OptionDefinition } from '@cloudscape-design/components/internal/components/option/interfaces';
import { DocumentFilter } from '../Chat/documentFilter';
import { useAlert } from '../../../context/alertContext';

import { v4 as uuidv4 } from 'uuid';
import { ProjectForm } from '../projectForm';
import { DocumentContext } from '../../../context/documentContext';
import { useSearchParams } from 'react-router-dom';

interface IStandards {
  category: string;
  examples: {
    label: string;
    value: string;
  }[];
  comingSoon: boolean;
}

const standards: IStandards[] = [
  {
    category: 'ISO/IEC Standards',
    examples: [
      {
        label: 'ISO/IEC 27001 - Mandatory Clauses',
        value: 'ISO-27001-stage1'
      },
      {
        label: 'ISO/IEC 27001 - Annex A',
        value: 'ISO-27001-stage2'
      }
    ],
    comingSoon: false
  },
  {
    category: 'NIST Standards',
    examples: [
      { label: 'NIST CSF', value: 'NIST CSF' },
      { label: 'NIST SP 800-53', value: 'NIST SP 800-53' }
    ],
    comingSoon: true
  },
  {
    category: 'Health & Safety (USA)',
    examples: [
      { label: 'OSHA 1910', value: 'OSHA 1910' },
      { label: 'OSHA 1926', value: 'OSHA 1926' }
    ],
    comingSoon: true
  },
  {
    category: 'Manufacturing Standards',
    examples: [
      { label: 'ISO 45001', value: 'ISO 45001' },
      { label: 'IATF 16949', value: 'IATF 16949' }
    ],
    comingSoon: true
  },
  {
    category: 'Cyber Security',
    examples: [
      { label: 'CIS Controls', value: 'CIS Controls' },
      { label: 'SOC 2', value: 'SOC 2' }
    ],
    comingSoon: true
  },
  {
    category: 'Banking & Finance',
    examples: [
      { label: 'Basel III', value: 'Basel III' },
      { label: 'FFIEC', value: 'FFIEC' }
    ],
    comingSoon: true
  }
];

const numQuestions = [
  { label: 'ISO/IEC 27001 - Mandatory Clauses', questions: 29 },
  { label: 'ISO/IEC 27001 - Annex A', questions: 93 }
];

export const GapAnalysis = () => {
  const { auth, token, user } = useContext(UserContext);
  const { mainProjects, allFolders } = useContext(ProjectContext);
  const { getAllDocs, allDocuments } = useContext(DocumentContext);
  const { setAlert, setSubsAlert, handleError } = useAlert();

  const [selectedProject, setSelectedProject] = useState<OptionDefinition>();
  const [selectedFolder, setSelectedFolder] = useState<OptionDefinition>();
  const [selectedStandard, setSelectedStandard] = useState<OptionDefinition>();
  const [showModal, setShowModal] = useState(false);
  const [showCoinsModal, setShowCoinsModal] = useState<{
    visible: boolean;
    type: string;
  }>({ visible: false, type: '' });
  const [folderModal, setFolderModal] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [localProjects, setLocalProjects] = useState<OptionDefinition[]>([]);
  const [localFolders, setLocalFolders] = useState<OptionDefinition[]>([]);
  const [standardOptions, setStandardOptions] = useState<OptionDefinition[]>(
    []
  );
  const [newDocumentName, setNewDocumentName] = useState<string>('');
  const [addProjectLoading, setAddProjectLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);

  // for document filters
  const [selectedDocuments, setSelectedDocuments] = useState<
    OptionDefinition[]
  >([]);
  const [kbFilters, setKBFilters] = useState<OptionDefinition[]>([]);
  const [KBChecked, setKBChecked] = useState<boolean>(false);
  const [similarityThreshold, setSimilarThreshold] = useState<number>(0.8);
  const [prevChecked, setPrevChecked] = useState<boolean>(false);
  const [showNoDocsModal, setShowNoDocsModal] = useState<boolean>(false);

  const [searchParams] = useSearchParams();

  const createProjectOptions = () => {
    const locProjects = mainProjects
      .filter((ele) => ele.projectType === 'information_request')
      .map((ele) => {
        return {
          label: ele.projectName,
          value: ele._id
        };
      });
    locProjects.push({ label: 'Create New Project', value: '' });
    setLocalProjects(locProjects);
  };
  useEffect(() => {
    reset();
    createProjectOptions();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    createProjectOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainProjects]);
  function reset() {
    setSelectedProject(undefined);
    setSelectedFolder(undefined);
    setSelectedStandard(undefined);
    setSelectedDocuments([]);
  }

  // useEffect(() => {
  //   if (!(allDocuments.length > 0)) {
  //     setShowNoDocsModal(true);
  //   }
  // }, [allDocuments]);

  const createOptionsForStandard = (category: string) => {
    const [thisStandard] = standards.filter((ele) => ele.category === category);

    setStandardOptions(thisStandard.examples);
  };

  useEffect(() => {
    if (selectedProject?.label === 'Create New Project') {
      setFolderModal(true);
    }
    const projFolders = allFolders.reduce((acc, curr) => {
      if (curr.project === selectedProject?.value) {
        acc.push({ label: curr.folderName, value: curr._id });
      }
      return acc;
    }, [] as OptionDefinition[]);

    setLocalFolders(projFolders);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject]);

  const blankOption = { label: '', value: '' };
  const resetState = () => {
    setSelectedFolder(blankOption);
    setSelectedDocuments([]);
    setSelectedProject(blankOption);
    setPage(1);
    setNewDocumentName('');
    setSelectedStandard(blankOption);
  };

  const startGap = async () => {
    setLoading(true);
    const searchDocs = {
      KBChecked,
      kbFilters,
      prevChecked,
      selectedDocuments: selectedDocuments.map((ele) => ele.value),
      thresholdScore: 0.8
    };
    try {
      const requestId = uuidv4();
      // const canProceed = await calculateCoins(requestId);
      const numberOfQuestions = numQuestions.filter(
        (ele) => ele.label === selectedStandard?.label
      )[0].questions;
      const haveEnoughCredit = await axios.post(
        `${process.env.REACT_APP_KORU_API}/credit?type=verifyCreditHold`,
        {
          type: 'GapAnalysis',
          documentIds: searchDocs.selectedDocuments,
          kbChecked: searchDocs.KBChecked,
          numOfQuestions: numberOfQuestions ?? 0
        },
        auth
      );
      let canProceed = false;
      if (haveEnoughCredit?.data?.result?.message === 'Have Enough Credit') {
        canProceed = true;
      }

      if (canProceed) {
        const docCreateResult = await axios.post(
          `${process.env.REACT_APP_KORU_API}/gap-analysis`,
          {
            searchDocs: {
              KBChecked,
              kbFilters,
              prevChecked,
              selectedDocuments: selectedDocuments.map((ele) => ele.value),
              thresholdScore: 0.8
            },
            document: {
              folder: selectedFolder?.value,
              project: selectedProject?.value,
              documentFileName: newDocumentName,
              documentType: 'GapAnalysis',
              fileExt: 'xlsx'
            },
            isoKey: selectedStandard?.value,
            requestId
          },
          auth
        );
        console.log('gap start result', docCreateResult);

        setLoading(false);
        setShowModal(false);
        resetState();
        getAllDocs();
        setAlert('GAP Analysis Started', 'success', 3);
      } else {
        setShowCoinsModal({ visible: true, type: 'BuyMore' });
        setLoading(false);
      }
    } catch (e) {
      const error = e as Error;
      setAlert(error.message, 'error');
    }
  };

  const createNewProject = async (
    projectName: string,
    projectDescription: string,
    projectType: string
  ) => {
    try {
      setAddProjectLoading(true);
      const createProject = await axios.post(
        `${process.env.REACT_APP_KORU_API}/folder?type=createNewProject`,
        {
          projectName,
          projectDescription,
          projectType,
          owner: user.name
        },
        auth
      );
      // getAllProjects('create new ptv 108');
      getAllDocs();

      setAddProjectLoading(false);
      setFolderModal(false);

      setAlert('Project Created', 'success', 2);
    } catch (e) {
      const error = e as Error;
      handleError(error);
      console.log('Error', error.message);
    }
  };

  const determineModalOptions = (ele: IStandards) => {
    if (!ele.comingSoon) {
      createOptionsForStandard(ele.category);
      const validDestinationProject =
        mainProjects.filter((p) => p.projectType === 'information_request')
          .length > 0;
      const validSourceDocs =
        allDocuments.filter((d) => d.documentType === 'analysis').length > 0;
      if (!validSourceDocs) {
        setShowNoDocsModal(true);
        return;
      }

      if (
        validDestinationProject &&
        localProjects.length > 0 &&
        validSourceDocs
      ) {
        setShowModal(true);
        return;
      }
      if (!validDestinationProject) {
        setFolderModal(true);
      }
    } else {
      return;
    }
  };

  return (
    <div className="p-4 space-y-8">
      <div className="p-4 bg-white rounded-2xl mt-4">
        <h1 className="text-3xl bold text-center p-4 ">Gap Assessments</h1>
        <p className="text-center">
          Generate a AI completed questionnaire based on your selected documents
          and chosen standard.
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 hover:cursor-pointer">
        {standards.map((ele) => (
          <div
            onClick={() => {
              determineModalOptions(ele);
            }}
            key={ele.category}
            className="relative bg-white rounded-lg shadow-md p-6 border border-gray-200 hover:shadow-lg transition-shadow"
          >
            {ele.comingSoon && (
              <span className="absolute top-2 right-2 bg-yellow-200 text-yellow-800 text-xs font-semibold px-2 py-1 rounded">
                Coming Soon
              </span>
            )}
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              {ele.category}
            </h3>
            <ul className="list-disc list-inside text-gray-600">
              {ele.examples.map((ex) => (
                <li key={ex.label}>{ex.label}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <Modal
        visible={showModal && !folderModal}
        onDismiss={() => setShowModal(false)}
        footer={
          <div className={`flex justify-end space-x-4`}>
            <Button
              onClick={() => {
                setSelectedProject({ label: '', value: '' });
                setSelectedFolder({ label: '', value: '' });
                setShowModal(false);
                resetState();
              }}
            >
              Cancel
            </Button>
            {page === 1 && (
              <Button
                loading={loading}
                disabled={
                  !selectedFolder ||
                  !selectedProject ||
                  !newDocumentName ||
                  !selectedStandard
                }
                onClick={() => {
                  setPage(2);
                }}
              >
                Next
              </Button>
            )}
            {page === 2 && (
              <Button
                loading={loading}
                disabled={!(selectedDocuments.length > 0 || KBChecked)}
                onClick={() => {
                  startGap();
                }}
              >
                Run GAP Analysis{' '}
              </Button>
            )}
          </div>
        }
      >
        <div className={`space-y-4`}>
          {page === 1 && (
            <>
              <h2 className="text-bold text-xl text-center">
                Select a Project for your Gap Assessment
              </h2>
              <p>This assessment will generate a questionnaire</p>
              <p>Gap assessments must be saved in a Questionnaire Project</p>
              <FormField label="Standard">
                <Select
                  selectedOption={selectedStandard ?? { label: '', value: '' }}
                  options={standardOptions}
                  onChange={({ detail }) =>
                    setSelectedStandard(detail.selectedOption)
                  }
                ></Select>
              </FormField>
              <FormField label="Questionnaire Project">
                <Select
                  selectedOption={selectedProject ?? { label: '', value: '' }}
                  options={localProjects}
                  onChange={({ detail }) =>
                    setSelectedProject(detail.selectedOption)
                  }
                ></Select>
              </FormField>
              {selectedProject && (
                <FormField label="Questionnaire Folder">
                  <Select
                    selectedOption={selectedFolder ?? { label: '', value: '' }}
                    options={localFolders}
                    onChange={({ detail }) =>
                      setSelectedFolder(detail.selectedOption)
                    }
                  ></Select>
                </FormField>
              )}
              {selectedFolder && (
                <FormField
                  label="File Name"
                  description="Enter a name for the generated file"
                >
                  <Input
                    value={newDocumentName}
                    onChange={({ detail }) => setNewDocumentName(detail.value)}
                  ></Input>
                </FormField>
              )}
            </>
          )}
          {page === 2 && (
            <>
              <h2>Select Document to Compare</h2>
              <DocumentFilter
                KBChecked={KBChecked}
                setKBChecked={setKBChecked}
                kbFilters={kbFilters}
                setKBFilters={setKBFilters}
                prevChecked={prevChecked}
                setPrevChecked={setPrevChecked}
                selectedDocuments={selectedDocuments}
                setSelectedDocuments={setSelectedDocuments}
                similarityThreshold={similarityThreshold}
                setSimilarThreshold={setSimilarThreshold}
                type={'search'}
              />
            </>
          )}
        </div>
      </Modal>
      <Modal
        visible={showCoinsModal.visible}
        onDismiss={() => setShowCoinsModal({ visible: false, type: '' })}
      >
        <>
          {showCoinsModal.type === 'BuyMore' ? (
            <div>
              <p className="text-sm text-gray-700 mb-4">
                You do not have enough Compliance Credits to perform this
                analysis. To continue using advanced features like document
                analysis and AI answers, additional coins are required.
              </p>
              {user?.isAdmin ? (
                <Button
                  onClick={() => {
                    setShowCoinsModal({ visible: false, type: '' });
                    setShowModal(false);
                    setSubsAlert('', 'coins');
                  }}
                  // className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded"
                >
                  Buy More Coins
                </Button>
              ) : (
                <button
                  className="bg-gray-400 text-white font-medium py-2 px-4 rounded cursor-not-allowed"
                  disabled
                >
                  Contact your administrator to purchase more coins
                </button>
              )}
            </div>
          ) : null}
          {showCoinsModal.type === 'Warning' ? (
            <div>
              <p className="text-sm text-gray-700 mb-4">
                You are running low on compliance credits. To continue using
                advanced features like document analysis and AI answers, we
                suggest buying additional coins to prevent interruptions.
              </p>
              {user?.isAdmin ? (
                <p className="text-sm text-gray-700 mb-4">
                  Navigate to the "View Credit Usage" page to purchase more.
                </p>
              ) : (
                <p className="text-sm text-gray-700 mb-4">
                  Contact you administrator to have them purchase more.
                </p>
              )}
            </div>
          ) : null}
        </>
      </Modal>
      <Modal
        visible={folderModal}
        size="max"
        onDismiss={() => {
          setFolderModal(false);
          // setShowTourEvents({ ...showTourEvents, projectModal: false });
        }}
      >
        <ProjectForm
          createNewProject={createNewProject}
          setFolderModal={setFolderModal}
          projectType={'information_request'}
          origin="gap"
        />
      </Modal>
      <Modal
        visible={showNoDocsModal}
        onDismiss={() => setShowNoDocsModal(false)}
      >
        <div>
          <h1 className="text-2xl pb-4">No Analyzed Source Documents</h1>
          <p className="text-sm text-gray-700 mb-4">
            You do not currently have any analyzed documents to search. Please
            create a project and upload documents so that you can search them.
          </p>
        </div>
      </Modal>
    </div>
  );
};
